import { useState, useEffect, useMemo, useCallback, memo } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from 'recharts';
import './TrendAnalysis.css';
import { 
  FaChartLine, 
  FaArrowDown, 
  FaArrowUp, 
  FaExclamationTriangle, 
  FaLightbulb,
  FaSpinner 
} from 'react-icons/fa';

const TrendAnalysis = memo(({ selectedState, selectedDistrict }) => {
  const [trendsData, setTrendsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('extraction');
  const [showPredictions, setShowPredictions] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadTrendsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, selectedDistrict]);

  const loadTrendsData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/groundwater-trends.json');
      
      if (!response.ok) {
        throw new Error('Failed to load trends data');
      }
      
      const allTrends = await response.json();
      
      let locationKey = 'INDIA';
      if (selectedState && selectedDistrict) {
        locationKey = `${selectedDistrict.toUpperCase()}, ${selectedState.toUpperCase()}`;
      } else if (selectedState) {
        locationKey = selectedState.toUpperCase();
      }
      
      const locationData = allTrends[locationKey];
      
      if (!locationData) {
        throw new Error(`No data available for ${locationKey}`);
      }
      
      const enrichedData = {
        location: locationKey,
        metrics: locationData,
        insights: generateInsights(locationData, locationKey)
      };
      
      console.log('=== TREND DATA LOADED ===');
      console.log('Location:', locationKey);
      console.log('Metrics available:', Object.keys(locationData));
      console.log('Sample data:', locationData.extraction.data.slice(0, 2));
      console.log('========================');
      
      setTrendsData(enrichedData);
    } catch (err) {
      console.error('Error loading trends:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const generateInsights = (metrics, location) => {
    const insights = [];
    const recommendations = [];

    try {
      if (!metrics.extraction || !metrics.extractionRatio) {
        return { insights, recommendations };
      }

      const extractionPattern = metrics.extraction.pattern;
      const extractionChange = metrics.extraction.avgChange;
      const extractionRatioChange = metrics.extractionRatio.avgChange;

      if (extractionPattern === 'severe_decline') {
        insights.push({
          type: 'positive',
          icon: <FaArrowDown />,
          title: 'Significant Reduction in Extraction',
          message: `Groundwater extraction in ${location} has decreased by ${Math.abs(extractionChange).toFixed(1)}% annually.`
        });
        recommendations.push('Continue conservation practices.');
      } else if (extractionPattern === 'moderate_decline') {
        insights.push({
          type: 'positive',
          icon: <FaArrowDown />,
          title: 'Declining Extraction Trend',
          message: `Extraction declining at ${Math.abs(extractionChange).toFixed(1)}% per year.`
        });
        recommendations.push('Maintain conservation efforts.');
      } else if (extractionPattern === 'strong_growth' || extractionPattern === 'moderate_growth') {
        insights.push({
          type: 'warning',
          icon: <FaArrowUp />,
          title: 'Rising Extraction Levels',
          message: `Extraction increasing at ${extractionChange.toFixed(1)}% annually.`
        });
        recommendations.push('Implement stricter extraction controls.');
      }

      const currentRatio = metrics.extractionRatio.data[metrics.extractionRatio.data.length - 1]?.value || 0;
      const predictedRatio = metrics.extractionRatio.predictions[2]?.value || 0;

      if (extractionRatioChange > 2) {
        insights.push({
          type: 'critical',
          icon: <FaExclamationTriangle />,
          title: 'Critical: Extraction Ratio Increasing',
          message: `Ratio growing at ${extractionRatioChange.toFixed(1)}%/year. Current: ${currentRatio.toFixed(1)}%, Predicted: ${predictedRatio.toFixed(1)}%.`
        });
        recommendations.push('URGENT: Implement immediate conservation measures.');
      } else if (currentRatio > 90) {
        insights.push({
          type: 'critical',
          icon: <FaExclamationTriangle />,
          title: 'Over-Exploitation Alert',
          message: `${location} extracting ${currentRatio.toFixed(1)}% of available resources.`
        });
        recommendations.push('Declare critical zone and enforce limits.');
      }

      if (metrics.rainfall) {
        const rainfallPattern = metrics.rainfall.pattern;
        const rainfallChange = metrics.rainfall.avgChange;

        if (rainfallPattern === 'severe_decline' || rainfallPattern === 'moderate_decline') {
          insights.push({
            type: 'warning',
            icon: <FaArrowDown />,
            title: 'Declining Rainfall Pattern',
            message: `Rainfall decreasing at ${Math.abs(rainfallChange).toFixed(1)}%/year.`
          });
          recommendations.push('Enhance rainwater harvesting.');
        }
      }

      if (extractionRatioChange < -2 && currentRatio < 70) {
        insights.push({
          type: 'positive',
          icon: <FaLightbulb />,
          title: 'Sustainable Management Success',
          message: `${location} successfully managing groundwater.`
        });
        recommendations.push('Document and share best practices.');
      }
    } catch (err) {
      console.error('Error generating insights:', err);
    }

    return { insights, recommendations };
  };

  const getMetricLabel = useCallback((metric) => {
    const labels = {
      extraction: 'Groundwater Extraction (ham)',
      extractable: 'Extractable Resources (ham)',
      rainfall: 'Annual Rainfall (mm)',
      extractionRatio: 'Extraction Ratio (%)'
    };
    return labels[metric] || metric;
  }, []);

  const getMetricColor = useCallback((metric) => {
    const colors = {
      extraction: '#ea2328',
      extractable: '#1fc21f',
      rainfall: '#2ba6ff',
      extractionRatio: '#ff8d1a'
    };
    return colors[metric] || '#666';
  }, []);

  // Compute derived values with useMemo (must be before any returns)
  const currentMetric = trendsData?.metrics?.[selectedMetric];
  
  const chartData = useMemo(() => {
    if (!currentMetric) return [];
    return showPredictions ? currentMetric.combinedData : currentMetric.data;
  }, [showPredictions, currentMetric]);
  
  const metricColor = useMemo(() => 
    getMetricColor(selectedMetric),
    [selectedMetric, getMetricColor]
  );
  
  const chartKey = useMemo(() => 
    `${selectedMetric}-${showPredictions ? 'pred' : 'hist'}-${chartData.length}`,
    [selectedMetric, showPredictions, chartData.length]
  );

  // Early returns after all hooks
  if (loading) {
    return (
      <div className="trend-loading">
        <FaSpinner className="trend-loading-spinner" />
        <p>Loading trend analysis...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trend-error">
        <FaExclamationTriangle />
        <p>Error: {error}</p>
        <button onClick={loadTrendsData} className="retry-button">Retry</button>
      </div>
    );
  }

  if (!trendsData || !trendsData.metrics) {
    return (
      <div className="trend-error">
        <p>No trend data available.</p>
      </div>
    );
  }
  
  if (!currentMetric || !currentMetric.data || currentMetric.data.length === 0) {
    return (
      <div className="trend-error">
        <p>No data available for this metric.</p>
      </div>
    );
  }

  return (
    <div className="trend-analysis">
      <div className="trend-header">
        <div className="trend-title">
          <FaChartLine className="trend-icon" />
          <h2>Trend Analysis: {trendsData.location}</h2>
        </div>
        <p className="trend-subtitle">
          Multi-year groundwater trends and predictions
        </p>
      </div>

      {/* Metric Selector */}
      <div className="trend-controls">
        <div className="metric-selector">
          <label>Select Metric:</label>
          <div className="metric-buttons">
            {Object.keys(trendsData.metrics).map(metric => (
              <button
                key={metric}
                className={`metric-btn ${selectedMetric === metric ? 'active' : ''}`}
                onClick={() => setSelectedMetric(metric)}
                style={{
                  backgroundColor: selectedMetric === metric ? getMetricColor(metric) : 'white',
                  color: selectedMetric === metric ? 'white' : '#333',
                  borderColor: getMetricColor(metric)
                }}
              >
                {getMetricLabel(metric).split('(')[0].trim()}
              </button>
            ))}
          </div>
        </div>
        <div className="prediction-toggle">
          <label>
            <input
              type="checkbox"
              checked={showPredictions}
              onChange={(e) => setShowPredictions(e.target.checked)}
            />
            Show 3-Year Predictions
          </label>
        </div>
      </div>



      {/* Charts Section */}
      <div className="trend-charts">
        {/* Line Chart */}
        <div className="chart-container" key={`line-container-${chartKey}`}>
          <h3>Trend Over Time</h3>
          <div style={{ width: '100%', height: '450px' }}>
            <ResponsiveContainer width="100%" height="100%" debounce={1}>
              <LineChart 
                data={chartData} 
                margin={{ top: 30, right: 40, left: 20, bottom: 80 }}
              >
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="year" 
                type="category"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={1}
                tick={{ fontSize: 11, fill: '#666', fontWeight: 500 }}
                tickMargin={10}
              />
              <YAxis 
                tick={{ fontSize: 11, fill: '#666' }}
                domain={['dataMin - 100', 'dataMax + 100']}
                width={80}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '2px solid #ccc',
                  borderRadius: '8px',
                  padding: '10px'
                }}
                formatter={(value) => [value.toFixed(2), getMetricLabel(selectedMetric)]}
              />
              <Legend wrapperStyle={{ paddingTop: '10px' }} />
              <Line
                type="monotone"
                dataKey="value"
                name={getMetricLabel(selectedMetric)}
                stroke={metricColor}
                strokeWidth={3}
                fill="none"
                connectNulls={false}
                isAnimationActive={false}
                dot={{ 
                  r: 6, 
                  fill: metricColor, 
                  stroke: '#fff', 
                  strokeWidth: 2
                }}
                activeDot={{ 
                  r: 8, 
                  fill: metricColor, 
                  stroke: '#fff', 
                  strokeWidth: 3 
                }}
              />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="chart-container" key={`bar-container-${chartKey}`}>
          <h3>Year-over-Year Change (%)</h3>
          {currentMetric.yoyChanges && currentMetric.yoyChanges.length > 0 ? (
            <div style={{ width: '100%', height: '450px' }}>
              <ResponsiveContainer width="100%" height="100%" debounce={1}>
                <BarChart 
                  data={currentMetric.yoyChanges} 
                  margin={{ top: 30, right: 40, left: 20, bottom: 80 }}
                >
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis 
                  dataKey="year" 
                  type="category"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={1}
                  tick={{ fontSize: 11, fill: '#666', fontWeight: 500 }}
                  tickMargin={10}
                />
                <YAxis 
                  tick={{ fontSize: 11, fill: '#666' }}
                  domain={[(dataMin) => Math.min(dataMin - 5, -2), (dataMax) => Math.max(dataMax + 5, 2)]}
                  width={80}
                  label={{ value: 'Change (%)', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666' } }}
                  tickFormatter={(value) => `${value.toFixed(1)}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '2px solid #ccc',
                    borderRadius: '8px',
                    padding: '10px'
                  }}
                  formatter={(value) => [`${value.toFixed(2)}%`, 'Change']}
                />
                <Legend wrapperStyle={{ paddingTop: '10px' }} />
                <Bar 
                  dataKey="change" 
                  name="Year-over-Year Change (%)" 
                  radius={[8, 8, 0, 0]}
                  isAnimationActive={false}
                  minPointSize={2}
                >
                  {currentMetric.yoyChanges.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.change === 0 ? '#999' : entry.change < 0 ? '#ea2328' : '#1fc21f'}
                    />
                  ))}
                </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="no-data">No year-over-year data available</div>
          )}
        </div>
      </div>

      {/* Statistics Summary */}
      <div className="trend-stats">
        <div className="stat-card">
          <div className="stat-label">Average Annual Change</div>
          <div className={`stat-value ${currentMetric.avgChange < 0 ? 'negative' : 'positive'}`}>
            {currentMetric.avgChange > 0 ? '+' : ''}{currentMetric.avgChange}%
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pattern Detected</div>
          <div className="stat-value pattern">
            {currentMetric.pattern.replace(/_/g, ' ').toUpperCase()}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">3-Year Prediction</div>
          <div className="stat-value">
            {currentMetric.predictions[2]?.value?.toFixed(2) || 'N/A'}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Current Value</div>
          <div className="stat-value">
            {currentMetric.data[currentMetric.data.length - 1]?.value?.toFixed(2) || 'N/A'}
          </div>
        </div>
      </div>

      {/* Insights Section */}
      {trendsData.insights.insights.length > 0 && (
        <div className="trend-insights">
          <h3>
            <FaLightbulb /> Key Insights
          </h3>
          <div className="insights-grid">
            {trendsData.insights.insights.map((insight, index) => (
              <div key={index} className={`insight-card ${insight.type}`}>
                <div className="insight-icon">{insight.icon}</div>
                <div className="insight-content">
                  <h4>{insight.title}</h4>
                  <p>{insight.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommendations Section */}
      {trendsData.insights.recommendations.length > 0 && (
        <div className="trend-recommendations">
          <h3>
            <FaExclamationTriangle /> Recommended Actions
          </h3>
          <ul className="recommendations-list">
            {trendsData.insights.recommendations.map((rec, index) => (
              <li key={index}>
                <span className="rec-bullet">→</span>
                {rec}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Methodology Note */}
      <div className="trend-methodology">
        <h4>Methodology</h4>
        <p>
          <strong>Trend Analysis:</strong> Year-over-year changes from historical data (2019-2025).
          <br />
          <strong>Predictions:</strong> Linear regression with R² validation.
          <br />
          <strong>Pattern Detection:</strong> Automated analysis identifying trends and volatility.
        </p>
      </div>
    </div>
  );
});

TrendAnalysis.displayName = 'TrendAnalysis';

export default TrendAnalysis;
